import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';

export interface SettlementPharmaInfoResponse {
  pharmaCompanyId: number;
  pharmaCompanyName: string;
  settlementYearMonth: string;
  prescriptionYearMonth: string;
  dataEnteredAt: string;
  isUploaded: boolean;
}

export interface SettlementUserSearchRequest {
  settlementYearMonth?: string;
  email?: string;
  companyName?: string;
  ceoName?: string;
  settlementStatus?: string;
}

export interface SettlementUserInfoResponse {
  prescriptionYearMonth: string;
  settlementYearMonth: string;
  userId: number;
  email: string;
  ceoName: string;
  phoneNumber: string;
  totalSettlementAmount: number;
}

export interface DeleteUserSettlementParams {
  prescriptionYearMonth: string;
  settlementYearMonth: string;
  userId: number;
}

export interface SettlementPharmaListItemResponse {
  prescriptionYearAndMonth?: string;
  prescriptionYearMonth?: string;
  phcmNm?: string;
  pharmaCompanyName?: string;
  totalSettlementAmount: number;
}

export interface SettlementClinicListItemResponse {
  prescriptionYearAndMonth?: string;
  prescriptionYearMonth?: string;
  hospNm?: string;
  clinicName?: string;
  totalSettlementAmount: number;
}

export interface SettlementUserDetailsResponse {
  settlementId: number;
  prescriptionYearMonth: string;
  settlementYearMonth: string;
  email: string;
  ceoName: string;
  phoneNumber: string;
  pharmaCompanyName: string;
  clinicName: string;
  drugName: string;
  drugCode: string;
  price: number;
  totalQuantity: number;
  totalAmount: number;
  prescriptionApprovalStatus: string;
  pharmaCompanySettlementStatus: string;
  commissionRate: number;
  finalAmount: number;
  remarks: string;
  parentCompanyId?: number | null;
  parentCompanyName?: string | null;
  settlementCompanyId?: number | null;
  settlementCompanyName?: string | null;
  /** 레거시 상세 컬럼 — 응답에 없으면 UI에서 '-' */
  basicFeeType?: string;
  isDetailFeeApplied?: boolean;
  standardCommissionRate?: number;
  platformFeeRate?: number;
}

export interface SettlementStatusResponse {
  isUploaded: boolean;
  isGenerated: boolean;
  isPharmaMerged: boolean;
  isPublished: boolean;
}

export interface PharmaCompanySimpleResponse {
  pharmaCompanyId: number;
  pharmaCompanyName: string;
}

export interface SettlementPharmaDetailResponse {
  pharmaCompanyId: number;
  pharmaCompanyName: string;
  settlementYearMonth: string;
  prescriptionYearMonth: string;
  dataEnteredAt: string;
  isUploaded: boolean;
}

export interface SettlementPharmaDrugInfoResponse {
  settlementPharmaId: number;
  prescriptionYearMonth: string;
  settlementYearMonth: string;
  pharmaCompanyId: number;
  clinicId: number;
  clinicName: string;
  userId: number;
  drugCode: string;
  drugName: string;
  price: number;
  totalQuantity: number;
  totalAmount: number;
  pharmaCompanySettlementStatus: string;
  remarks: string;
  confirmedRequestAt: string;
  confirmedAt: string;
  pharmaDataStatus: string;
}

export interface SettlementPublishRequest {
  settlementYearMonth: string;
}

export interface MergePharmaSettlementRequest {
  settlementYearMonth: string;
}

export interface SettlementGenerateRequest {
  settlementYearMonth: string;
}

export interface SettlementPharmaUploadRow {
  userId: number;
  userEmail: string;
  prescriptionYearMonth: string;
  settlementYearMonth: string;
  hospitalId: number;
  clinicName: string;
  drugCode: string;
  pharmaCompanyId: number;
  pharmaCompanyName: string;
  drugPrice: number;
  totalQuantity: number;
  totalAmount: number;
  finalAmount?: number;
  pharmaCompanySettlementStatus: string;
  remarks?: string;
}

export interface SettlementPharmaStatusUpdateRequest {
  settlementPharmaIds: number[];
  status: string;
}

export const settlementService = {
  getSettlementPharma: async (
    yearMonth: string,
    pageable: Pageable,
    pharmaCompanyId?: number,
  ): Promise<PageResponse<SettlementPharmaInfoResponse>> => {
    const params: Record<string, string | number> = {
      yearMonth,
      page: pageable.page,
      size: pageable.size,
    };
    if (pharmaCompanyId != null) {
      params.pharmaCompanyId = pharmaCompanyId;
    }
    const response = await apiClient.get(createApiPath.settlements.list(), {
      params,
    });
    return response.data;
  },

  getUserSettlementInfos: async (
    searchRequest: SettlementUserSearchRequest,
  ): Promise<SettlementUserInfoResponse[]> => {
    const params: Record<string, string> = {};
    if (searchRequest.settlementYearMonth) {
      params.settlementYearMonth = searchRequest.settlementYearMonth;
    }
    if (searchRequest.email) params.email = searchRequest.email;
    if (searchRequest.companyName) params.companyName = searchRequest.companyName;
    if (searchRequest.ceoName) params.ceoName = searchRequest.ceoName;
    if (searchRequest.settlementStatus) {
      params.settlementStatus = searchRequest.settlementStatus;
    }
    const response = await apiClient.get(createApiPath.settlements.users(), {
      params,
    });
    return response.data;
  },

  getPharmaSettlementInfos: async (
    searchRequest: SettlementUserSearchRequest & {
      pharmaName?: string;
    },
  ): Promise<SettlementPharmaListItemResponse[]> => {
    const params: Record<string, string> = {};
    if (searchRequest.settlementYearMonth) {
      params.settlementYearMonth = searchRequest.settlementYearMonth;
    }
    if (searchRequest.pharmaName) params.pharmaName = searchRequest.pharmaName;
    if (searchRequest.settlementStatus) {
      params.settlementStatus = searchRequest.settlementStatus;
    }
    const response = await apiClient.get(createApiPath.settlements.pharmas(), {
      params,
    });
    return response.data;
  },

  getClinicSettlementInfos: async (
    searchRequest: SettlementUserSearchRequest & {
      clinicName?: string;
    },
  ): Promise<SettlementClinicListItemResponse[]> => {
    const params: Record<string, string> = {};
    if (searchRequest.settlementYearMonth) {
      params.settlementYearMonth = searchRequest.settlementYearMonth;
    }
    if (searchRequest.clinicName) params.clinicName = searchRequest.clinicName;
    if (searchRequest.settlementStatus) {
      params.settlementStatus = searchRequest.settlementStatus;
    }
    const response = await apiClient.get(createApiPath.settlements.clinics(), {
      params,
    });
    return response.data;
  },

  getUserSettlementDetails: async (
    prescriptionYearMonth: string,
    settlementYearMonth: string,
    userEmail: string,
  ): Promise<SettlementUserDetailsResponse[]> => {
    const response = await apiClient.get(
      createApiPath.settlements.userDetails(),
      {
        params: { prescriptionYearMonth, settlementYearMonth, userEmail },
      },
    );
    return response.data;
  },

  getSettlementStatus: async (
    settlementYearMonth: string,
  ): Promise<SettlementStatusResponse> => {
    const response = await apiClient.get(createApiPath.settlements.status(), {
      params: { settlementYearMonth },
    });
    return response.data;
  },

  updateSettlementPharmaStatuses: async (
    request: SettlementPharmaStatusUpdateRequest,
  ): Promise<void> => {
    await apiClient.patch(createApiPath.settlements.status(), request);
  },

  getPharmaCompanies: async (
    yearMonth: string,
  ): Promise<PharmaCompanySimpleResponse[]> => {
    const response = await apiClient.get(
      createApiPath.settlements.pharmaCompanies(),
      { params: { yearMonth } },
    );
    return response.data;
  },

  /** 레거시 `getSettlementPharmaDetail` — settlementYearMonth + prescriptionYearMonth 필수 */
  getSettlementPharmaDetail: async (
    settlementYearMonth: string,
    prescriptionYearMonth: string,
    pharmaCompanyId: number,
  ): Promise<SettlementPharmaDetailResponse> => {
    const response = await apiClient.get(createApiPath.settlements.detail(), {
      params: {
        settlementYearMonth,
        prescriptionYearMonth,
        pharmaCompanyId,
      },
    });
    return response.data;
  },

  /** 레거시 `getSettlementUploadPhcmDetails` — settlementYearMonth + prescriptionYearMonth 필수 */
  getSettlementDrugs: async (
    settlementYearMonth: string,
    prescriptionYearMonth: string,
    pharmaCompanyId: number,
  ): Promise<SettlementPharmaDrugInfoResponse[]> => {
    const response = await apiClient.get(
      createApiPath.settlements.detailDrugs(),
      {
        params: {
          settlementYearMonth,
          prescriptionYearMonth,
          pharmaCompanyId,
        },
      },
    );
    return response.data;
  },

  deletePharmaSettlements: async (
    settlementPharmaIds: number[],
  ): Promise<void> => {
    await apiClient.delete(createApiPath.settlements.detailDrugs(), {
      data: { settlementPharmaIds },
    });
  },

  deleteUserSettlement: async (
    params: DeleteUserSettlementParams,
  ): Promise<void> => {
    await apiClient.delete(createApiPath.settlements.deleteUserSettlement(), {
      params,
    });
  },

  publishSettlement: async (
    request: SettlementPublishRequest,
  ): Promise<void> => {
    await apiClient.post(createApiPath.settlements.publish(), request);
  },

  mergePharmaSettlementData: async (
    request: MergePharmaSettlementRequest,
  ): Promise<void> => {
    await apiClient.post(createApiPath.settlements.mergePharma(), request);
  },

  generateSettlementData: async (
    request: SettlementGenerateRequest,
  ): Promise<void> => {
    await apiClient.post(createApiPath.settlements.generate(), request);
  },

  exportUserSettlements: async ({
    settlementYearMonth,
    email,
  }: {
    settlementYearMonth: string;
    email?: string;
  }): Promise<SettlementUserDetailsResponse[]> => {
    const response = await apiClient.get<SettlementUserDetailsResponse[]>(
      createApiPath.settlements.export(),
      {
        params: {
          settlementYearMonth,
          ...(email ? { email } : {}),
        },
      },
    );
    return response.data;
  },

  getSettlementMonths: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>(
      createApiPath.settlements.months(),
    );
    return response.data;
  },

  uploadPharmaSettlements: async (
    rows: SettlementPharmaUploadRow[],
  ): Promise<void> => {
    await apiClient.post(createApiPath.settlements.pharmaUpload(), rows);
  },
};
